import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StylePane } from 'configuration-schemas/viewdefns/viewdefn';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { AsyncCellState, PendingRedecorateActionPayload } from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import {
  matchStyleOrStyleColorToItem,
  updateNodeAsyncState,
} from 'src/components/ConfigurableGrid/utils/ConfigurableGrid.utils';
import { get, isEmpty, isNil, merge } from 'lodash';
import type { ThunkApi } from 'src/store.types';
import { resolveStylePaneConfig } from './HistoryStylePane.types';
import { getActiveContext } from 'src/pages/PerspectiveSelection/PerspectiveSelection.selectors';

export const HISTORY_STYLE_PANE_HISTORY_MAX = 5;

interface HistoryStylePaneItem {
  memberId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  item: Record<string, any>;
}

export interface HistoryStylePaneSlice {
  viewDefn?: StylePane;
  /** Represents an array of the `StyleColor` data associated with the selected `Style`.
   * This data structure is tightly coupled to `HistoryStylePane.tsx` component state `selectedPosition`,
   * which is an number indicating the currently rendered array index from `currentItem` */
  currentItem: BasicPivotItem[] | null;
  history: HistoryStylePaneItem[];
  loading: boolean;
}

const initialState: HistoryStylePaneSlice = {
  viewDefn: undefined,
  currentItem: null,
  history: [],
  loading: false,
};

/** Redecorate from another component's request
 * redecorate all colors as it's fast and easy that way
 */
export const handleExternalRedecorate = createAsyncThunk<
  BasicPivotItem[],
  PendingRedecorateActionPayload,
  ThunkApi & { rejectValue: string }
>(
  'HistoryStylePane/handleExternalRedecorate',
  // the payload itself is only consumed by the pending/fulfilled/rejected reducers (via
  // `meta.arg`) to scope which cells get async-state markers; the thunk redecorates every row
  async (_payload, { getState, rejectWithValue, extra }) => {
    const { currentItem } = getState().historyStylePane;
    if (isNil(currentItem) || isEmpty(currentItem)) {
      return rejectWithValue('Style pane has no rows to redecorate');
    }
    // The pane is only ever mounted from the right container, whose payload carries the
    // `isAssortmentBuild` flag that selects which pane defnId is in play
    const rightContainerPayload = getState().rightContainer.payload;
    const isAssortmentBuild = get(rightContainerPayload, 'isAssortmentBuild', false);
    const { dataDefnId, aggBy } = resolveStylePaneConfig(getActiveContext(getState())?.stylePane, isAssortmentBuild);

    try {
      const redecoratedData = await extra.pivotService.redecorate({
        coordinates: currentItem.map((item) => ({ product: item.id })),
        defnId: dataDefnId,
        nestData: false,
        aggBy,
      });
      if (isNil(redecoratedData)) {
        return rejectWithValue('Redecorate returned no data');
      }
      return redecoratedData;
    } catch (e) {
      extra.loggingService.error('Error refreshing Style Pane', (e as Error).stack);
      return rejectWithValue((e as Error).message);
    }
  },
);

/** Apply redecorate to style pane items, matching on either style id or style color id
 */
const applyAsyncStateToAffectedRows = (
  state: HistoryStylePaneSlice,
  { styleIds, stylecolorIds, dataIndices }: PendingRedecorateActionPayload,
  asyncState: AsyncCellState,
) => {
  const currentItem = state.currentItem;
  if (isNil(currentItem)) {
    return;
  }
  const styleAndStyleColorIds = [...styleIds, ...stylecolorIds];
  currentItem.forEach((item, position) => {
    const productFound = styleAndStyleColorIds.some((id) => {
      return matchStyleOrStyleColorToItem(id, item);
    });
    if (!productFound) {
      return;
    }

    // we 'clean' these here as `StylePane` configs usually don't have `attribute:` or `member:` wrappers,
    // but other view's send updates with them.
    // So we 'clean' them off here, as we use the exact column string for detecting if the column needs to
    // animate the redecoration
    const cleanedDataIndices = dataIndices.map((di) =>
      di.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, ''),
    );

    // merge into a fresh object so we deep-clone the row (including its asyncstate map)
    // instead of mutating the existing redux node
    const updatedItem = merge({}, item);
    updateNodeAsyncState(updatedItem, [...dataIndices, ...cleanedDataIndices], asyncState);
    currentItem[position] = updatedItem;
  });
};

/** Splice redecorated rows back into `currentItem` in a single reducer pass. Each decorated row
 * is matched to its existing position by `id` and merged over the existing pivot data (incoming
 * redecorate data wins where present), the same way `localUpdateHistoryStylePaneItem` does.
 */
function mergeDecoratedItems(state: HistoryStylePaneSlice, decoratedItems: BasicPivotItem[]) {
  decoratedItems.forEach((decorated) => {
    if (isNil(state.currentItem)) return;
    const position = state.currentItem.findIndex((item) => item.id === decorated.id);
    if (position < 0) {
      return;
    }
    const oldItem = state.currentItem[position];
    state.currentItem[position] = merge(oldItem, decorated);
  });
}

const historyStylePaneSlice = createSlice({
  name: 'HistoryStylePane',
  initialState,
  reducers: {
    requestHistoryStylePane: (state) => {
      state.loading = true;
    },
    receiveHistoryStylePane: (state, { payload }: PayloadAction<BasicPivotItem[]>) => {
      state.currentItem = payload;
      state.loading = false;
    },
    receiveHistoryStylePaneTenantConfig: (state, { payload }: PayloadAction<StylePane>) => {
      state.viewDefn = payload;
    },
    moveItemToHistory: {
      reducer: (
        state,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { payload }: PayloadAction<{ memberId: string; item: Record<string, any> }>,
      ) => {
        const historyItem: HistoryStylePaneItem = {
          memberId: payload.memberId,
          item: payload.item,
        };
        const filteredHistory = state.history.filter((item) => item.memberId !== payload.memberId);
        state.history = [historyItem, ...filteredHistory.slice(0, HISTORY_STYLE_PANE_HISTORY_MAX)];
        state.currentItem = null;
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      prepare: (memberId: string, item: Record<string, any>) => ({ payload: { memberId, item } }),
    },
    /** `StylePane` data happens to be stored in redux,
     * so to reflect changes immediately we run this as edits are applied,
     * but before redecroate comes back and runs this again
     */
    localUpdateHistoryStylePaneItem: {
      reducer: (state, { payload }: PayloadAction<{ item: BasicPivotItem; position: number }>) => {
        if (state.currentItem == null) {
          return state;
        }
        const { position, item } = payload;
        if (position < 0 || position >= state.currentItem.length) {
          return state;
        }

        // We merge here because `StylePane` relies on pivot data,
        // which can't easily be redecorated at this time,
        // so we merge the data together, taking incoming redecorate data (where present),
        // as priority over existing properties
        const oldItem = state.currentItem[position];
        const mergedNewItem = merge(oldItem, item);
        // overwrite the object at `position`
        // FIXME this could be safer, object assignment like this can easily fail
        state.currentItem[position] = mergedNewItem;
      },
      prepare: (item: BasicPivotItem, position: number) => ({ payload: { item, position } }),
    },
    /** Redecorate refreshes every stylecolor the pane renders at once, so we splice all of the
     * decorated rows back into `currentItem` in a single reducer pass instead of dispatching a
     * `localUpdateHistoryStylePaneItem` per row.
     */
    localUpdateAllHistoryStylePaneItems: (state, { payload }: PayloadAction<BasicPivotItem[]>) => {
      if (state.currentItem == null) {
        return state;
      }
      mergeDecoratedItems(state, payload);
    },
    applyHistoryStylePaneAsyncState: {
      reducer: (
        state,
        { payload }: PayloadAction<{ dataIndexes: string[]; asyncState: AsyncCellState; position: number }>,
      ) => {
        if (state.currentItem == null) {
          return;
        }
        const { position, dataIndexes, asyncState } = payload;
        if (position < 0 || position >= state.currentItem.length) {
          return;
        }
        // clone the row (and its asyncstate map) so we don't mutate the existing
        // redux node, then stamp the async markers onto the copy
        const updatedItem = {
          ...state.currentItem[position],
          asyncstate: { ...state.currentItem[position].asyncstate },
        };
        updateNodeAsyncState(updatedItem, dataIndexes, asyncState);
        state.currentItem[position] = updatedItem;
      },
      prepare: (dataIndexes: string[], asyncState: AsyncCellState, position: number) => ({
        payload: { dataIndexes, asyncState, position },
      }),
    },
    receiveError: (_state, _action: PayloadAction<string>) => {
      // noop
    },
    cleanHistoryStylePaneUp: () => initialState,
  },
  extraReducers: (builder) => {
    // mark the externally-edited cells busy while the redecorate is in flight
    builder.addCase(handleExternalRedecorate.pending, (state, { meta }) => {
      applyAsyncStateToAffectedRows(state, meta.arg, AsyncCellState.Redecorating);
    });
    builder.addCase(handleExternalRedecorate.fulfilled, (state, { payload, meta }) => {
      if (state.currentItem == null) {
        return state;
      }
      mergeDecoratedItems(state, payload);
      // redecorated rows merge over the old ones (which still carry the Redecorating markers),
      // so clear the affected cells back to Idle
      applyAsyncStateToAffectedRows(state, meta.arg, AsyncCellState.Idle);
    });
    // failures are logged inside the thunk and the pane keeps its current rows,
    // but the affected cells still need their Redecorating markers cleared
    builder.addCase(handleExternalRedecorate.rejected, (state, { meta }) => {
      applyAsyncStateToAffectedRows(state, meta.arg, AsyncCellState.Idle);
    });
  },
});

export const {
  requestHistoryStylePane,
  receiveHistoryStylePane,
  receiveHistoryStylePaneTenantConfig,
  moveItemToHistory,
  localUpdateHistoryStylePaneItem,
  localUpdateAllHistoryStylePaneItems,
  applyHistoryStylePaneAsyncState,
  receiveError,
  cleanHistoryStylePaneUp,
} = historyStylePaneSlice.actions;

export default historyStylePaneSlice.reducer;
